import React, { useState } from "react";
import RegistryPage from "../components/RegistryPage";
import { RiskBadge, StatusBadge } from "../components/Badges";
import api from "../lib/api";
import { Btn } from "../components/Primitives";
import { Upload } from "lucide-react";
import { toast } from "sonner";

const ASSET_TYPES = ["application","database","saas","cloud","website","mobile","api","spreadsheet","legacy","file_store"];

function BulkUpload({ onDone }) {
  const [uploading, setUploading] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/data-assets/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Imported ${data.created} assets`);
      onDone?.();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };
  return (
    <label className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-md bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 cursor-pointer">
      <Upload className="w-4 h-4" />
      {uploading ? "Uploading..." : "Bulk Upload CSV"}
      <input data-testid="bulk-upload-input" type="file" accept=".csv" onChange={up} className="hidden" />
    </label>
  );
}

export default function DataInventory() {
  return (
    <RegistryPage
      title="Data Inventory"
      description="Register applications, databases, SaaS tools, and other systems that store personal data."
      endpoint="/data-assets"
      testIdPrefix="data-assets"
      reportKey="inventory"
      extraHeaderActions={<BulkUpload onDone={() => window.location.reload()} />}
      columns={[
        { key: "name", label: "Asset" },
        { key: "asset_type", label: "Type", width: "110px" },
        { key: "country", label: "Country", width: "110px" },
        { key: "cross_border_transfer", label: "Cross-Border", width: "110px" },
        { key: "risk_rating", label: "Risk", width: "100px", render: (i) => <RiskBadge level={i.risk_rating} /> },
        { key: "status", label: "Status", width: "100px", render: (i) => <StatusBadge status={i.status} /> },
      ]}
      fields={[
        { key: "name", label: "Asset name", required: true },
        { key: "asset_type", label: "Type", type: "select", options: ASSET_TYPES },
        { key: "description", label: "Description", type: "textarea", full: true },
        { key: "business_owner", label: "Business owner" },
        { key: "technical_owner", label: "Technical owner" },
        { key: "hosting_location", label: "Hosting location" },
        { key: "cloud_provider", label: "Cloud provider" },
        { key: "country", label: "Country" },
        { key: "data_categories", label: "Data categories (comma)" },
        { key: "principal_categories", label: "Principal categories (comma)" },
        { key: "contains_child_data", label: "Contains children's data", type: "checkbox" },
        { key: "cross_border_transfer", label: "Cross-border transfer", type: "checkbox" },
        { key: "encryption_at_rest", label: "Encryption at rest", type: "select", options: ["yes","no","unknown"] },
        { key: "encryption_in_transit", label: "Encryption in transit", type: "select", options: ["yes","no","unknown"] },
        { key: "access_control_status", label: "Access control", type: "select", options: ["yes","no","unknown"] },
        { key: "logging_status", label: "Logging", type: "select", options: ["yes","no","unknown"] },
        { key: "backup_status", label: "Backup", type: "select", options: ["yes","no","unknown"] },
        { key: "retention_period_days", label: "Retention (days)", type: "number" },
        { key: "risk_rating", label: "Risk", type: "select", options: ["low","medium","high","critical"] },
        { key: "status", label: "Status", type: "select", options: ["active","retired"] },
      ]}
      defaultItem={{ name: "", asset_type: "application", country: "India", data_categories: [], principal_categories: [], contains_child_data: false, cross_border_transfer: false, encryption_at_rest: "unknown", encryption_in_transit: "unknown", access_control_status: "unknown", logging_status: "unknown", backup_status: "unknown", risk_rating: "medium", status: "active" }}
    />
  );
}
